/**
 * Creates a filter handler function that can be used across multiple modules
 *
 * @param {Object} config Configuration object for the filter
 * @param {Function} config.setLoading
 * @param {Function} [config.setSubmittedData] Function to set submitted data (optional)
 * @param {Function} config.setResults Function to set results (e.g., setStocks)
 * @param {Function} config.showNotification Function to show notifications
 * @param {Function} config.handleApiError Function to handle complex API errors
 * @param {Function} config.httpClient
 * @param {string} config.endpoint API endpoint
 * @param {Object} config.fieldMappings Mapping of data fields to API fields and operators
 * @param {Object} [config.rangeFields={}] Configuration for range fields (optional)
 * @param {Object} [config.valueTransformers={}] Functions to transform field values before sending (optional)
 * @returns {Function} Filter handler function
 */
export const createFilterHandler = (config) => {
  const {
    setLoading,
    setSubmittedData,
    setResults,
    showNotification,
    handleApiError,
    httpClient,
    endpoint,
    fieldMappings,
    rangeFields = {}, // Default to empty object if not provided
    valueTransformers = {}, // Default to empty object if not provided
    refresh,
  } = config;

  return async (filterData) => {
    try {
      setLoading(true);

      const cleanedData = cleanFilterData(filterData);
      if (setSubmittedData) {
        setSubmittedData(cleanedData);
      }

      const queryParams = buildQueryParams(
        cleanedData,
        fieldMappings,
        rangeFields,
        valueTransformers
      );
      const apiUrl = `${endpoint}?${queryParams}`;

      const { data } = await httpClient.get(apiUrl);

      if (data.status === 200) {
        setResults(data.data);
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };
};

/**
 * Cleans filter data by removing empty strings, null, and undefined values
 *
 * @param {Object} data The filter data object
 * @returns {Object} Cleaned filter data
 */
export const cleanFilterData = (data) => {
  return Object.fromEntries(
    Object.entries(data).filter(([_, value]) => {
      if (typeof value === "string") return value.trim() !== "";
      return value !== null && value !== undefined;
    })
  );
};

/**
 * Builds query parameters from filter data
 *
 * @param {Object} data Cleaned filter data
 * @param {Object} fieldMappings Field mappings configuration
 * @param {Object} [rangeFields={}] Range fields configuration (optional)
 * @param {Object} [valueTransformers={}] Value transformer functions (optional)
 * @returns {string} URL query parameters string
 */
export const buildQueryParams = (
  data,
  fieldMappings,
  rangeFields = {},
  valueTransformers = {}
) => {
  const params = new URLSearchParams();

  // Filters with specific field names and operators
  Object.entries(fieldMappings).forEach(([dataField, [apiField, operator]]) => {
    if (data[dataField] !== undefined) {
      // Apply transformer if one exists for this field, otherwise use the value directly
      const value = valueTransformers[dataField]
        ? valueTransformers[dataField](data[dataField])
        : data[dataField];
      // we may need to send only field name in come cases that don't take operator
      params.append(
        operator ? `filters[${apiField}][${operator}]` : apiField,
        value
      );
    }
  });

  // Process range filters if provided
  if (Object.keys(rangeFields).length > 0) {
    Object.entries(rangeFields).forEach(
      ([baseField, { apiField, minField, maxField }]) => {
        const min = data[minField];
        const max = data[maxField];

        if (min !== undefined || max !== undefined) {
          // Apply transformers if they exist for range fields
          const minValue = valueTransformers[minField]
            ? valueTransformers[minField](min)
            : min;

          const maxValue = valueTransformers[maxField]
            ? valueTransformers[maxField](max)
            : max;

          // Only add min value if it exists and is not empty
          if (minValue !== undefined && minValue !== "") {
            params.append(`filters[${apiField}][$between][0]`, minValue);
          }

          // Only add max value if it exists and is not empty
          if (maxValue !== undefined && maxValue !== "") {
            params.append(`filters[${apiField}][$between][1]`, maxValue);
          }
        }
      }
    );
  }

  return params.toString();
};
